import {
  type Account,
  type Media,
  type Platform,
  type Post,
  type Subscription,
  type User,
  type Workspace,
  type BillingCycle,
  SubscriptionPlan,
  type WorkspaceRole,
  type ApprovalStatus,
  type JoinRequestStatus,
} from '../types';
import * as api from '../api/workspaceApi';

export interface WorkspaceListenerCallbacks {
  onUpdate: (workspaces: Workspace[]) => void;
  onError?: (error: unknown) => void;
}

// Polling interval in ms
const POLLING_INTERVAL = 5000;

export const listenToUserWorkspaces = async (
  userId: string,
  { onUpdate, onError }: WorkspaceListenerCallbacks
): Promise<() => void> => {
  let active = true;

  const fetch = async () => {
    if (!active) return;
    try {
      const workspaces = await api.fetchUserWorkspaces();
      if (active) {
        onUpdate(workspaces);
      }
    } catch (error) {
      if (active && onError) {
        onError(error);
      }
    }
  };

  // Initial fetch
  void fetch();

  // Poll
  const intervalId = setInterval(fetch, POLLING_INTERVAL);

  return () => {
    active = false;
    clearInterval(intervalId);
  };
};

export const addPostToWorkspace = async (workspaceId: string, post: Omit<Post, 'id'>): Promise<string> => {
  // We cast to Post because the API might expect a full object or we let the backend handle ID generation
  // Ideally API takes Partial<Post>
  const newPost = await api.createPost(workspaceId, post);
  return newPost.id;
};

export const updatePostInWorkspace = async (workspaceId: string, postId: string, updates: Partial<Post>): Promise<void> => {
  // TODO: Add Update Post API
  // await api.updatePost(workspaceId, postId, updates);
  console.warn('updatePostInWorkspace not fully implemented locally');
};

export const deletePostFromWorkspace = async (workspaceId: string, postId: string): Promise<void> => {
  // TODO: Add Delete Post API
  // await api.deletePost(workspaceId, postId);
  console.warn('deletePostFromWorkspace not fully implemented locally');
};

export const addMediaToWorkspace = async (workspaceId: string, media: Omit<Media, 'id'>): Promise<string> => {
  // Helper to create media via API if we add that endpoint, for now stub or warn
  // But App.tsx expects a string return (ID)
  console.warn('addMediaToWorkspace not fully implemented locally - returning temp ID');
  return 'temp-media-id-' + Date.now();
};

export const deleteMediaFromWorkspace = async (workspaceId: string, mediaId: string): Promise<void> => {
  // TODO: Add Media API
  console.warn('deleteMediaFromWorkspace not fully implemented locally');
};

export const updateMediaInWorkspace = async (workspaceId: string, mediaId: string, updates: Partial<Media>): Promise<void> => {
  console.warn('updateMediaInWorkspace not fully implemented locally');
};

export const addAccountToWorkspace = async (workspaceId: string, account: Account): Promise<void> => {
  // Map internal frontend Account type to API SocialAccount type if needed
  // Removing invalid property access that caused lint errors
  await api.createSocialAccount(workspaceId, {
      id: account.id,
      platform: account.platform,
      username: account.username,
      workspace_id: workspaceId
  });
};

export const deleteAccountFromWorkspace = async (workspaceId: string, accountId: string): Promise<void> => {
  await api.deleteAccount(workspaceId, accountId);
};

export const addWorkspaceMember = async (workspaceId: string, email: string): Promise<void> => {
  console.warn('Member management removed');
};

export const removeWorkspaceMember = async (workspaceId: string, userId: string): Promise<void> => {
  console.warn('Member management removed');
};

export const setWorkspaceMemberRole = async (workspaceId: string, userId: string, role: string): Promise<void> => {
  console.warn('Member management removed');
};

export const clearWorkspaceMemberRole = async (workspaceId: string, userId: string): Promise<void> => {
  console.warn('Member management removed');
};

export const incrementWorkspaceMonthlyPostCount = async (workspaceId: string, _monthIso?: string): Promise<void> => {
  // Managed by backend automatically on post creation usually
};

export const createApprovalRequest = async (workspaceId: string, request: { postId: string; requesterId: string; comment?: string }): Promise<string> => {
  await api.createApprovalRequest(workspaceId, request);
  return 'temp-id';
};

export const updateApprovalRequest = async (workspaceId: string, requestId: string, updates: { status: ApprovalStatus; approverId: string; comment?: string }): Promise<void> => {
  await api.updateApprovalRequest(workspaceId, requestId, updates.status);
};

export const submitWorkspaceJoinRequest = async (workspaceId: string, request: { userId: string; email: string; name?: string; message?: string; requestedRole?: WorkspaceRole }): Promise<void> => {
  console.warn('Join Requests not implemented locally');
};

export const approveWorkspaceJoinRequest = async (workspaceId: string, userId: string, approverId: string, options?: { role?: WorkspaceRole; comment?: string }): Promise<void> => {
    console.warn('Join Requests not implemented locally');
};

export const declineWorkspaceJoinRequest = async (workspaceId: string, userId: string, approverId: string, comment?: string): Promise<void> => {
    console.warn('Join Requests not implemented locally');
};

export const renameWorkspace = async (workspaceId: string, newName: string): Promise<void> => {
  await api.renameWorkspace(workspaceId, newName);
};

export const deleteWorkspaceAndData = async (workspaceId: string): Promise<void> => {
  await api.deleteWorkspace(workspaceId);
};
